import {
  Button,
  Card,
  Checkbox,
  HeartCalendarIcon,
  Input,
  Label,
} from "@nfp-tracker/components";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@nfp-tracker/components";
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@nfp-tracker/utils";
import { ROUTES } from "../constants/routes";
import { ensureUserBaselineRows } from "../integrations/supabase/domain";
import { supabase } from "../integrations/supabase/client";
import { trackEvent } from "../integrations/analytics/client";
import { buildAuthRedirectUrl } from "../utils/authRedirect";
import { resetNonReadyOwnerContext } from "../hooks/useOwnerContext";
import { buildOnboardingPath, resolvePostSignInRoute } from "../utils/postSignInRoute";

type AuthMode = "login" | "signup";

type AuthProps = {
  mode: AuthMode;
};

const Auth = ({ mode }: AuthProps) => {
  const termsUrl = (import.meta.env.VITE_TERMS_URL as string | undefined) ?? TERMS_OF_USE_URL;
  const privacyUrl = (import.meta.env.VITE_PRIVACY_URL as string | undefined) ?? PRIVACY_POLICY_URL;
  const isSignUp = mode === "signup";
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("inviteToken")?.trim() || "";
  const inviteEmail = searchParams.get("email")?.trim().toLowerCase() || "";
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isSettingNewPassword, setIsSettingNewPassword] = useState(false);
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const onboardingPath = buildOnboardingPath(inviteToken);

  const buildSignupConfirmPath = (targetEmail: string) => {
    const query = new URLSearchParams({ email: targetEmail.trim() });
    if (inviteToken) {
      query.set("inviteToken", inviteToken);
    }
    return `${ROUTES.signupConfirmEmail}?${query.toString()}`;
  };

  const continueToApp = useCallback(async (userId: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user?.id) {
      await ensureUserBaselineRows({
        userId: user.id,
        email: user.email,
        firstName: (user.user_metadata?.first_name as string | undefined) || null,
        lastName: (user.user_metadata?.last_name as string | undefined) || null,
      });
    }

    const { route } = await resolvePostSignInRoute(userId, { inviteToken });
    // Never let a pre-sign-in "unauthenticated" / "needs-onboarding" context bounce the user.
    await resetNonReadyOwnerContext(queryClient);
    navigate(route, { replace: true });
  }, [inviteToken, navigate, queryClient]);

  useEffect(() => {
    let isMounted = true;

    const checkResetToken = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get("access_token");
      const type = hashParams.get("type");

      if (accessToken && type === "recovery") {
        setIsSettingNewPassword(true);
      }
    };

    const redirectIfLoggedIn = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!isMounted || !session) {
        return;
      }

      await continueToApp(session.user.id);
    };

    if (!isSignUp) {
      void checkResetToken();
    }

    void redirectIfLoggedIn();

    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session || !isMounted || isSettingNewPassword) {
        return;
      }

      await continueToApp(session.user.id);
    });

    return () => {
      isMounted = false;
      listener?.subscription.unsubscribe();
    };
  }, [continueToApp, isSignUp, isSettingNewPassword]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: buildAuthRedirectUrl(ROUTES.login),
      });

      if (error) throw error;

      trackEvent("auth_password_reset_requested", {
        hasInviteToken: Boolean(inviteToken),
      });

      toast({
        title: "Check your email",
        description:
          "We've sent you a password reset link. Click the link in the email to set a new password.",
      });
      setIsResetPassword(false);
      setEmail("");
    } catch (error: unknown) {
      const message = typeof error === "object" && error && "message" in error ? (error as { message?: string }).message : String(error);
      trackEvent("auth_password_reset_failed", {
        errorType: classifyAuthError(message || ""),
        hasInviteToken: Boolean(inviteToken),
      });
      toast({
        title: "Something didn't work — let's try that again",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      trackEvent("auth_password_reset_completed", {
        hasInviteToken: Boolean(inviteToken),
      });

      toast({
        title: "Password updated!",
        description:
          "Your password has been successfully reset. You can now sign in with your new password.",
      });

      // Clear the hash from URL and reset states
      window.history.replaceState(null, "", ROUTES.login);
      setIsSettingNewPassword(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: unknown) {
      const message = typeof error === "object" && error && "message" in error ? (error as { message?: string }).message : String(error);
      trackEvent("auth_password_reset_complete_failed", {
        errorType: classifyAuthError(message || ""),
        hasInviteToken: Boolean(inviteToken),
      });
      toast({
        title: "Something didn't work — let's try that again",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isUnconfirmedEmailError = (message: string) =>
    /email.*confirm|confirm.*email|not confirmed/i.test(message);

  const resendSignupConfirmation = async () => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: {
        emailRedirectTo: buildAuthRedirectUrl(buildSignupConfirmPath(email)),
      },
    });

    if (error) throw error;
  };

  const normalizeError = (error: unknown) => {
    if (error && typeof error === "object" && "message" in error) {
      return (error as { message?: string }).message || "Something didn't work — let's try that again";
    }

    return "Something went wrong. Please try again.";
  };

  const classifyAuthError = (message: string) => {
    if (/confirm/i.test(message)) {
      return "email_unconfirmed";
    }

    if (/invalid login credentials/i.test(message)) {
      return "invalid_credentials";
    }

    if (/already exists/i.test(message)) {
      return "account_exists";
    }

    if (/trial has ended/i.test(message)) {
      return "trial_ended";
    }

    return "unknown";
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    setLoading(true);

    try {
      if (isSignUp) {
        trackEvent("auth_sign_up_started", {
          hasInviteToken: Boolean(inviteToken),
        });

        if (!acceptedTerms) {
          trackEvent("auth_sign_up_failed", {
            errorType: "terms_required",
            hasInviteToken: Boolean(inviteToken),
          });
          toast({
            title: "Terms Required",
            description:
              "Please accept the Terms of Service and Privacy Policy to continue.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        // Check if email already exists
        const { data: existingUser, error: existingUserError } = await supabase
          .from("user_accounts")
          .select("user_id")
          .eq("email", email)
          .maybeSingle();
        if (existingUserError) {
          throw existingUserError;
        }
        if (existingUser) {
          trackEvent("auth_sign_up_failed", {
            errorType: "account_exists",
            hasInviteToken: Boolean(inviteToken),
          });
          toast({
            title: "Account already exists",
            description: "An account with this email already exists. Please sign in or reset your password.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: buildAuthRedirectUrl(buildSignupConfirmPath(email)),
            data: {
              first_name: firstName,
              last_name: lastName,
            },
          },
        });

        if (error) throw error;

        if (data.user && !data.session) {
          trackEvent("auth_sign_up_succeeded", {
            emailConfirmationRequired: true,
            hasInviteToken: Boolean(inviteToken),
          });
          navigate(buildSignupConfirmPath(email));
          setLoading(false);
          return;
        }

        if (data.session) {
          trackEvent("auth_sign_up_succeeded", {
            emailConfirmationRequired: false,
            hasInviteToken: Boolean(inviteToken),
          });
          toast({
            title: "Account created!",
            description: "Welcome to NFP Tracker. Let's set up your profile.",
          });
          navigate(onboardingPath);
        }
        setLoading(false);
        return;
      } else {
        trackEvent("auth_sign_in_started", {
          hasInviteToken: Boolean(inviteToken),
        });

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error || !data.session) {
          const signInMessage = error?.message || "Something didn't work — let's try that again";

          if (isUnconfirmedEmailError(signInMessage)) {
            try {
              await resendSignupConfirmation();
              trackEvent("auth_email_confirmation_resent", {
                hasInviteToken: Boolean(inviteToken),
              });
              navigate(buildSignupConfirmPath(email));
              return;
            } catch (resendError: unknown) {
              const resendMessage = normalizeError(resendError);
              throw new Error(
                `Your email isn't confirmed yet. We couldn't resend confirmation automatically: ${resendMessage}`
              );
            }
          }

          throw new Error(signInMessage);
        }

        // Same destination as continueToApp (which the SIGNED_IN event also runs), so the two
        // can't race each other to different pages.
        const { route, mustOnboard } = await resolvePostSignInRoute(data.session.user.id, {
          inviteToken,
        });
        trackEvent("auth_sign_in_succeeded", {
          hasInviteToken: Boolean(inviteToken),
          needsOnboarding: mustOnboard,
        });
        await resetNonReadyOwnerContext(queryClient);
        navigate(route);
      }
    } catch (error: unknown) {
      const message = normalizeError(error);
      let displayMessage = message;
      trackEvent(isSignUp ? "auth_sign_up_failed" : "auth_sign_in_failed", {
        errorType: classifyAuthError(message),
        hasInviteToken: Boolean(inviteToken),
      });

      if (/free trial has ended/i.test(message)) {
        navigate(ROUTES.subscription);
        displayMessage = "Your free trial has ended. Please subscribe to continue.";
      }

      setAuthError(displayMessage);

      toast({
        title: "Something didn't work — let's try that again",
        description: displayMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center p-4 bg-gradient-to-b from-background to-muted relative">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary blur-3xl animate-pulse" />
        <div
          className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-accent blur-3xl animate-pulse [animation-delay:1s]"
        />
      </div>

      <Card className="compact-density-mobile w-full max-w-md p-8 space-y-6 shadow-[var(--shadow-medium)] animate-fade-in relative z-10">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-6">
            <div className="relative overflow-hidden rounded-[28px]">
              <HeartCalendarIcon className="w-32 h-32 object-cover" />
            </div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">NFP Tracker</h1>
          <p className="text-muted-foreground">
            {isSettingNewPassword
              ? "Set your new password"
              : isSignUp
              ? "Natural family planning on easy mode"
              : "Welcome back"}
          </p>
        </div>

        {isSettingNewPassword ? (
          <form onSubmit={handleSetNewPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="h-12"
                placeholder="At least 6 characters"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="h-12"
                placeholder="Re-enter your password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base"
              disabled={loading}
            >
              {loading ? "Updating..." : "Set New Password"}
            </Button>
          </form>
        ) : isResetPassword ? (
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                placeholder="Enter your email address"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleAuth} className="space-y-4">
            {authError && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm px-3 py-2">
                {authError}
              </div>
            )}
            {isSignUp && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="h-12"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12"
              />
            </div>
            {isSignUp && (
              <div className="flex items-start space-x-2 pt-2">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) =>
                    setAcceptedTerms(checked as boolean)
                  }
                />
                <Label
                  htmlFor="terms"
                  className="text-sm leading-tight cursor-pointer"
                >
                  I accept the {" "}
                  {termsUrl ? (
                    <a
                      href={termsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      Terms of Service
                    </a>
                  ) : (
                    <span>Terms of Service</span>
                  )}{" "}
                  and {" "}
                  {privacyUrl ? (
                    <a
                      href={privacyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      Privacy Policy
                    </a>
                  ) : (
                    <span>Privacy Policy</span>
                  )}
                </Label>
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-12 text-base"
              disabled={loading}
            >
              {loading
                ? "Please wait..."
                : isSignUp
                ? "Create Account"
                : "Sign In"}
            </Button>
          </form>
        )}

        {!isSettingNewPassword && (
          <div className="text-center text-sm space-y-2">
            {!isResetPassword && !isSignUp && (
              <button
                type="button"
                onClick={() => {
                  setAuthError("");
                  setIsResetPassword(true);
                }}
                className="text-primary hover:underline block w-full"
              >
                Forgot password?
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setIsResetPassword(false);
                setAuthError("");
                navigate(isSignUp ? ROUTES.login : ROUTES.signup);
              }}
              className="text-primary hover:underline"
            >
              {isResetPassword
                ? "Back to sign in"
                : isSignUp
                ? "Already have an account? Sign in"
                : "Don't have an account? Sign up"}
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Auth;
